import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Paper, Grid } from "@mui/material";

const ChequePayment = ({ total, onPaymentDataChange, currentPayment }) => {
  const [fields, setFields] = useState({
    amount: 0,
    chequeNumber: "",
    mobile: "",
    dateEcheance: "",
  });
  const [activeField, setActiveField] = useState("amount");
  const isInitialized = useRef(false);
  const isUpdatingFromParent = useRef(false);

  // Sync with currentPayment when it changes
  useEffect(() => {
    if (currentPayment) {
      console.log("Syncing fields with currentPayment:", currentPayment);

      // Set flag to prevent triggering onPaymentDataChange
      isUpdatingFromParent.current = true;

      setFields({
        amount: currentPayment.amount || 0,
        chequeNumber: currentPayment.chequeNumber || "",
        mobile: currentPayment.mobile || "",
        dateEcheance: currentPayment.dueDate || "",
      });

      // Reset flag after update
      setTimeout(() => {
        isUpdatingFromParent.current = false;
      }, 50);
    }
  }, [currentPayment]);

  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
    }

    const handleVirtualInput = (e) => {
      if (e.detail.method === "cheque") {
        // Don't trigger if we're updating from parent
        if (isUpdatingFromParent.current) {
          console.log("Ignoring virtual input during parent update");
          return;
        }

        const newFields = { ...fields, [e.detail.input]: e.detail.value };
        setFields(newFields);

        // Only call onPaymentDataChange if amount is not being cleared to 0 during deletion
        const newAmount = parseFloat(newFields.amount) || 0;
        if (
          !(
            e.detail.input === "amount" &&
            e.detail.value === "0" &&
            fields.amount === 0
          )
        ) {
          onPaymentDataChange({ ...newFields, amount: newAmount }, "cheque");
        }
      }
    };

    window.addEventListener("virtualKeyboardInput", handleVirtualInput);
    return () =>
      window.removeEventListener("virtualKeyboardInput", handleVirtualInput);
  }, [fields, total, onPaymentDataChange]);

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const order = ["amount", "chequeNumber", "mobile"];
      setActiveField(order[(order.indexOf(activeField) + 1) % order.length]);
      return;
    }

    let val = fields[activeField].toString();
    if (e.key === "Backspace") val = val.slice(0, -1);
    else if (/[0-9.]/.test(e.key)) val += e.key;

    const updated = { ...fields, [activeField]: val };
    setFields(updated);

    // Don't trigger if we're in the middle of a parent update
    if (!isUpdatingFromParent.current) {
      onPaymentDataChange(
        { ...updated, amount: parseFloat(updated.amount) || 0 },
        "cheque",
      );
    }
  };

  const RenderField = ({ label, field, value }) => (
    <Box
      mb={1.5}
      onClick={() => {
        setActiveField(field);
        window.dispatchEvent(
          new CustomEvent("virtualKeyboardFocus", {
            detail: { method: "cheque", input: field },
          }),
        );
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: "bold" }}>
        {label}
      </Typography>
      <Paper
        tabIndex={0}
        data-payment={field === "amount" ? "cheque" : "other"} // Add this line
        onKeyDown={handleKeyDown}
        sx={{
          p: 1,
          border:
            activeField === field ? "2px solid #f57c00" : "1px solid #ccc",
          outline: "none",
          "&:focus": { bgcolor: "#fff3e0" },
        }}
      >
        {value ? value : field === "amount" ? "0.000" : "-"}
      </Paper>
    </Box>
  );

  return (
    <Box sx={{ p: 1 }}>
      <RenderField
        label="Montant Chèque"
        field="amount"
        value={fields.amount}
      />
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <RenderField
            label="N° Chèque"
            field="chequeNumber"
            value={fields.chequeNumber}
          />
        </Grid>
        <Grid item xs={6}>
          <RenderField label="Mobile" field="mobile" value={fields.mobile} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ChequePayment;
